/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ContainerProps,
    ContainerRef,
    Instance,
    InstanceCreator,
    InstanceId,
    Scope,
} from './types'

import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'


import {PromiseModalContext} from './PromiseModalContext'
import {toggleModalState} from 'state/ui'
import {useAppDispatch} from 'hooks'

export const DEFAULT_SCOPE = 'stack'

const InstanceContainer: React.ForwardRefRenderFunction<
    ContainerRef,
    ContainerProps
  > = (props, ref) => {
      const {scope = DEFAULT_SCOPE} = props || {}

      const dispatch = useAppDispatch()
      const propsRef = useRef(props)

      const [instances, setInstances] = useState<{ [key: string]: Instance }>({})
      const [hashStack, setHashStack] = useState<string[]>([])

      const {
          promiseModalStack, setPromiseModalStack,
      } = useContext(PromiseModalContext)

      const register = (_scope: Scope, _ref: ContainerRef) => {
          setPromiseModalStack({
              ...promiseModalStack,
              [_scope]: _ref,
          })
      }

      const unregister = (_scope: Scope) => {
          const newStack = promiseModalStack
          delete newStack[_scope]
          setPromiseModalStack(newStack)
      }

      const resolve = useCallback(
          (hash: InstanceId, v: any) => instances?.[hash]?.resolve(v),
          [instances],
      )

      const resolveAll = useCallback(
          (v: any) => Object.values(instances).forEach((i) => i.resolve(v)),
          [instances],
      )

      const reject = useCallback(
          (hash: InstanceId, r: any) => instances?.[hash]?.reject(r),
          [instances],
      )

      const rejectAll = useCallback(
          (r: any) => Object.values(instances).forEach((i) => i.reject(r)),
          [instances],
      )

      const hasInstance = useCallback(
          (hash: InstanceId) => !!hashStack.find((id) => id === hash),
          [hashStack],
      )

      const getInstance = useCallback((hash: InstanceId) => instances?.[hash], [
          instances,
      ])


      const remove = (hash: InstanceId): void => {
          setHashStack((stack) => stack.filter((s) => s !== hash))
          dispatch(toggleModalState(false))

          setTimeout(() => {
              setInstances((_instances) => {
                  const {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      [hash]: _, ...omitHash
                  } = _instances

                  return omitHash
              })
          })

          const {onRemove} = props
          onRemove?.(hash)
      }

      const removeRef = useRef(remove)


      const create: InstanceCreator = (Component, options = {}, instanceProps) => new Promise((res, rej) => {
          const hash = instanceProps?.instanceId || Math.random().toString()
          dispatch(toggleModalState(true))

          const {
              onResolve,
              onReject,
          } = propsRef.current

          const instanceOptions = {
              instanceId: hash,
              ...options,
          }

          const instance: Instance = {
              Component,
              props: {
                  ...options,
                  ...instanceProps,
              },
              reject: (r) => {
                  removeRef.current(hash)
                  rej(r)
                  onReject?.(r, hash)
              },
              resolve: (v) => {
                  removeRef.current(hash)
                  res(v)
                  onResolve?.(v, hash)
              },
              ...instanceOptions,
          }

          setInstances((_instances) => ({
              [hash]: instance,
              ..._instances,
          }))

          setTimeout(() => {
              setHashStack((stack) => [...stack, hash])
              propsRef.current.onOpen?.(hash, instance)
          })
      })

      const createRef = useRef(create)

      useEffect(() => {
          propsRef.current = props
          removeRef.current = remove
          createRef.current = create
      })

      useImperativeHandle(ref, () => ({
          create: createRef.current,
          getInstance,
          hasInstance,
          reject,
          rejectAll,
          resolve,
          resolveAll,
      }))

      useEffect(() => {
          register(scope, {
              create: createRef.current,
              getInstance,
              hasInstance,
              reject,
              rejectAll,
              resolve,
              resolveAll,
          })

          return () => unregister(scope)
      }, [scope])

      const mapKeys = useMemo(() => {
          const keys = Object.keys(instances)

          return keys.map((key) => {
              const {
                  Component,
                  props: _props,
                  resolve: _resolve,
                  reject: _reject,
              } = instances[key]

              const isOpen = !!hashStack.find((h) => h === key)

              return (
                  <Component
                      {..._props}
                      isOpen={isOpen}
                      key={key}
                      onReject={_reject}
                      onResolve={_resolve}
                  />
              )
          })
      }, [instances, hashStack])

      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <>{mapKeys}</>
  }

export const Container = forwardRef(InstanceContainer)
